import ApiService from "../../services/ApiService";
import { getLocalHourToUTC, isPast } from "../../services/TimeService";
import translate from "../../services/Translate";
import { MeetingImportStructuredData } from "./MeetingImport.d";

/** Single source of truth for meeting import language options. Add a line here to support a new language. */
export const MEETING_IMPORT_LANGUAGES = [
  { locale: "fr-FR", translationKey: "PAGES.MEETING_IMPORT.LANGUAGES.FRENCH" },
  { locale: "en-US", translationKey: "PAGES.MEETING_IMPORT.LANGUAGES.ENGLISH" },
] as const;

export const getMeetingImportLanguageLabel = (locale: string | undefined, language: string): string => {
  if (!locale) return "—";
  const entry = MEETING_IMPORT_LANGUAGES.find((language) => language.locale === locale);
  return entry ? translate(language, entry.translationKey) : locale;
};

/**
 * Take payload from CSV, remove empty values, rename bad keys, and type correctly the existing values
 * @param data Input payload from CSV
 * @returns Payload for API request to create several meetings
 */
const _cleanDataBeforeSubmitting = (data: Array<MeetingImportStructuredData>) =>
  data.map((line) => ({
    ...line,
    animator: undefined,
    animators: line.animator?.trim().length
      ? line.animator.split(",").map((animator_email) => animator_email.trim())
      : undefined,
    cohort_id: undefined,
    cohort_ids: line.cohort_id?.trim().length
      ? line.cohort_id.split(",").map((cohort_id: string) => +cohort_id.trim())
      : undefined,
    project_id: line.project_id ? +line.project_id : undefined,
    program_id: line.program_id ? +line.program_id : undefined,
    language: line.language?.trim() || undefined,
    custom_meeting_link: line.custom_meeting_link.trim() || undefined,
    date: undefined,
    duration: +line.duration,
    name: line.name,
    start: undefined,
    start_date: line.date,
    start_hour: getLocalHourToUTC(line.start, line.date, true),
  }));

export const createImportedMeetings = (
  data: Array<MeetingImportStructuredData>,
  language: string,
  setSubmitting: Function,
  showAlert: Function,
  popAlert: Function,
  setCsvData: Function
) => {
  if (data.some((meeting) => isPast(`${meeting.date}T${meeting.start}`))) {
    showAlert?.({
      type: "danger",
      title: translate(language, "PAGES.MEETING_IMPORT.DST_ALERTS.PAST_MEETING_TITLE"),
      message: translate(language, "PAGES.MEETING_IMPORT.DST_ALERTS.PAST_MEETING_MESSAGE"),
      clickOutsideToClose: true,
      buttons: [
        {
          label: translate(language, "GLOBAL.CLOSE"),
          variant: "danger",
          onClick: () => popAlert?.(),
        },
      ],
    });
    return;
  }

  setSubmitting(true);

  showAlert?.({
    type: "info",
    title: translate(language, "PAGES.MEETING_IMPORT.DST_ALERTS.LOADING_TITLE"),
    message: translate(language, "PAGES.MEETING_IMPORT.DST_ALERTS.LOADING_MESSAGE"),
    clickOutsideToClose: false,
    buttons: [],
  });

  ApiService.post("meetings/batch", {
    meetings: _cleanDataBeforeSubmitting(data),
  })
    .then(() => {
      popAlert?.();

      showAlert?.({
        type: "success",
        title: translate(language, "PAGES.MEETING_IMPORT.DST_ALERTS.SUCCESS_TITLE"),
        message: translate(language, "PAGES.MEETING_IMPORT.DST_ALERTS.SUCCESS_MESSAGE"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "GLOBAL.OK"),
            onClick: () => {
              popAlert?.();
              setCsvData([]);
            },
          },
        ],
      });
    })
    .catch((err) => {
      popAlert?.();

      const data = (err as any)?.data;

      showAlert?.({
        type: "danger",
        title: translate(language, "PAGES.MEETING_IMPORT.DST_ALERTS.API_ERROR"),
        message: translate(language, "PAGES.MEETING_IMPORT.DST_ALERTS.ERROR_MESSAGE"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "GLOBAL.CLOSE"),
            variant: "danger",
            onClick: () => popAlert?.(),
          },
        ],
        extraContent: data ? (
          <pre className="text-start off-white-background p-2 rounded border">{JSON.stringify(data, null, 2)}</pre>
        ) : null,
      });
    })
    .finally(() => {
      setSubmitting(false);
    });
};

export const fetchAnimators = (setAnimators: Function, language: string) => {
  ApiService.get("meetings/resources?only_active_animators=true")
    .then((response) => setAnimators(response.data.meeting_staff_and_programs.animators))
    .catch((error) => {
      console.error("Failed to get animators:", error);
      alert(translate(language, "PAGES.MEETING_IMPORT.ERRORS.GET_ANIMATORS"));
    });
};

export const fetchCohorts = (setCohorts: Function, language: string) => {
  ApiService.get("cohorts")
    .then((response) => {
      setCohorts(
        response.data.cohorts.map((cohort: any) => ({
          id: cohort.id,
          name: cohort.name,
        }))
      );
    })
    .catch((error) => {
      console.error("Failed to get cohorts:", error);
      alert(translate(language, "PAGES.MEETING_IMPORT.ERRORS.GET_COHORTS"));
    });
};

export const fetchProjects = (setProjects: Function, language: string) => {
  ApiService.get("projects")
    .then((response) => {
      setProjects(
        response.data.projects.map((project: any) => ({
          id: project.id,
          name: project.name,
        }))
      );
    })
    .catch((error) => {
      console.error("Failed to get projects:", error);
      alert(translate(language, "PAGES.MEETING_IMPORT.ERRORS.GET_PROJECTS"));
    });
};

export const fetchPrograms = (setPrograms: Function, language: string) => {
  ApiService.get("meetings/resources?only_active_animators=true")
    .then((response) => setPrograms(response.data.meeting_staff_and_programs.programs))
    .catch((error) => {
      console.error("Failed to get programs:", error);
      alert(translate(language, "PAGES.MEETING_IMPORT.ERRORS.GET_PROGRAMS"));
    });
};
